import React from 'react';
import clsx from 'clsx';

import {i18n} from '@netx/core/i18n';

import {faTimes} from '@netx/core-icons';
import {Typography} from '@mui/material';
import {assetExpirationToDateString} from '@netx/core-asset-expiration/utils';
import {IconButton} from '@netx/core-components/IconButton';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {useAssetExpirationProvider} from '../../contexts';
import {
	assetExpirationBadgeClasses,
	getAssetExpirationBadgeUtilityClass,
	AssetExpirationBadgeClasses,
	ASSET_EXPIRATION_BADGE_CLASS_NAME,
} from './assetExpirationBadgeClasses';

export type AssetExpirationBadgeElement = HTMLDivElement;
export type AssetExpirationBadgeRef =
	React.ForwardedRef<AssetExpirationBadgeElement>;

type AssetExpirationUtiltiyClassProps = WithClasses<
	AssetExpirationBadgeClasses,
	{}
>;

const useUtilityClasses = (ownerState: AssetExpirationUtiltiyClassProps) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getAssetExpirationBadgeUtilityClass, classes);
};

const AssetExpirationBadgeRoot = styled('div', {
	name: ASSET_EXPIRATION_BADGE_CLASS_NAME,
	slot: 'Root',
})(({theme, ...props}) => ({
	alignItems: 'center',
	display: 'flex',
	background: theme.palette.error.main,
	color: theme.palette.error.contrastText,
	padding: theme.spacing(0.5, 1),
}));

const AssetExpirationBadgeText = styled(Typography, {
	name: ASSET_EXPIRATION_BADGE_CLASS_NAME,
	slot: 'Root',
})(({theme, ...props}) => ({
	flex: '1 1 auto',
	textAlign: 'center',
}));

const AssetExpirationBadgeAction = styled(IconButton, {
	name: ASSET_EXPIRATION_BADGE_CLASS_NAME,
	slot: 'Action',
})(({theme, ...props}) => ({
	flex: '0 0 auto',
}));

export interface AssetExpirationBadgeProps
	extends
		React.DetailedHTMLProps<
			React.HTMLAttributes<AssetExpirationBadgeElement>,
			AssetExpirationBadgeElement
		>,
		WithClasses<
			AssetExpirationBadgeClasses,
			{
				assetId: number;
				expirationDate: number;
			}
		> {}

const AssetExpirationBadgeInner = (
	{
		classes: _classes,
		className,
		expirationDate,
		assetId,
		...props
	}: AssetExpirationBadgeProps,
	ref?: AssetExpirationBadgeRef,
) => {
	const classes = useUtilityClasses({
		classes: _classes,
	});

	const assetExpirationProvider = useAssetExpirationProvider();

	const handleActionClick = (
		event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		event.stopPropagation();
		assetExpirationProvider.unexpire(assetId);
	};

	return (
		<AssetExpirationBadgeRoot
			ref={ref}
			{...props}
			className={clsx(classes.root, className)}
		>
			<AssetExpirationBadgeText variant="caption" noWrap>
				{`${i18n.t('i18n.expires')}: ${assetExpirationToDateString(
					expirationDate,
				)}`}
			</AssetExpirationBadgeText>
			<AssetExpirationBadgeAction
				color="inherit"
				edge="end"
				icon={faTimes}
				label={i18n.t('i18n.unexpire')}
				size="small"
				onClick={handleActionClick}
			/>
		</AssetExpirationBadgeRoot>
	);
};

export const AssetExpirationBadgePrimitive = React.forwardRef(
	AssetExpirationBadgeInner,
) as (
	props: AssetExpirationBadgeProps,
	ref?: AssetExpirationBadgeRef,
) => JSX.Element;

export const AssetExpirationBadge = React.memo(
	AssetExpirationBadgePrimitive,
) as (
	props: AssetExpirationBadgeProps,
	ref?: AssetExpirationBadgeRef,
) => JSX.Element;

export default AssetExpirationBadge;
